import {
  ProductBOM,
  BOMWithDetails,
  BOMFormData,
  BOMValidationError,
  BOMFilters,
} from "../types/bom.types";
import { BillOfMaterial, Ingredient } from "../pages/Menu/BOM/types";
import { ListResponse } from "../../../app.interface";
import axiosInstance from "../../../middlewares/axiosInstance";

interface ListBOMsParams {
  searchQuery?: string;
  filters?: any[];
  startDate?: number;
  endDate?: number;
  dateField?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex?: number;
  pageSize?: number;
}

const transformFormDataToBillOfMaterial = (
  formData: BOMFormData
): Partial<BillOfMaterial> => {
  return {
    name: formData.name,
    description: formData.description || "",
    ingredients: formData.ingredients.map(
      (ing): Ingredient => ({
        ...ing,
        parent_unit: ing.unit,
        selected_batch_id: ing.batch_id,
      })
    ),
    processed_items: formData.processed_items || [],
    preparation_steps: formData.preparation_steps || [],
    shelf_life_duration: formData.shelf_life_duration || 0,
    price: formData.price || 0,
  };
};

class BOMService {
  async listBOMs(
    params: ListBOMsParams
  ): Promise<ListResponse<BillOfMaterial>> {
    const response = await axiosInstance.post("/business-admin/bom/list", {
      ...params,
    });
    return response.data.data;
  }

  async getBOM(id: string): Promise<BillOfMaterial> {
    const response = await axiosInstance.post("/business-admin/bom/get", {
      _id: id,
    });
    return response.data.data;
  }

  async createBOM(data: BOMFormData): Promise<BillOfMaterial> {
    const transformedData = transformFormDataToBillOfMaterial(data);
    const response = await axiosInstance.post(
      "/business-admin/bom/create",
      transformedData
    );
    return response.data;
  }

  async updateBOM(id: string, data: BOMFormData): Promise<BillOfMaterial> {
    const transformedData = transformFormDataToBillOfMaterial(data);
    const response = await axiosInstance.post("/business-admin/bom/update", {
      _id: id,
      ...transformedData,
    });
    return response.data;
  }

  async deleteBOM(id: string): Promise<{ success: boolean }> {
    const response = await axiosInstance.post("/business-admin/bom/delete", {
      _id: id,
    });
    return response.data;
  }

  async getBOMByProduct(productId: string): Promise<BOMWithDetails[]> {
    const response = await axiosInstance.post(
      "/business-admin/bom/by-product",
      {
        product_id: productId,
      }
    );
    return response.data;
  }

  async validateBOM(data: BOMFormData): Promise<BOMValidationError[]> {
    const response = await axiosInstance.post(
      "/business-admin/bom/validate",
      data
    );
    return response.data;
  }

  async searchBOMs(filters: BOMFilters): Promise<{
    items: BOMWithDetails[];
    total: number;
  }> {
    const response = await axiosInstance.post(
      "/business-admin/bom/search",
      filters
    );
    return response.data;
  }

  async calculateCost(data: BOMFormData): Promise<{
    totalCost: number;
    breakdown: Array<{
      ingredient_product_id: string;
      quantity: number;
      unit_cost: number;
      total_cost: number;
    }>;
  }> {
    const response = await axiosInstance.post(
      "/business-admin/bom/calculate-cost",
      data
    );
    return response.data;
  }
}

export const bomService = new BOMService();
